import React from "react";
import PropTypes from "prop-types";
import { CALENDAR_CONSTANTS } from "../utils/constants";
import "./CustomCalendar.scss";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { getNowInTimezone } from "../utils/dateUtils";

/**
 * CustomCalendar - A fully responsive and reusable calendar component
 *
 * Features:
 * - Responsive design that adapts to any container size
 * - Configurable size variants (small, medium, large)
 * - Optional navigation and weekday headers
 * - Event indicators with custom colors
 * - Active date highlighting
 * - Touch-friendly on mobile devices
 *
 * @example
 * // Basic usage
 * <CustomCalendar
 *   currentMonth={new Date()}
 *   onPreviousMonth={() => setMonth(prev => subMonths(prev, 1))}
 *   onNextMonth={() => setMonth(prev => addMonths(prev, 1))}
 *   onDateClick={(date) => console.log('Selected:', date)}
 *   getEventsForDate={(date) => getEvents(date)}
 *   eventColors={{ meeting: '#ff0000', task: '#00ff00' }}
 * />
 *
 * @example
 * // Compact calendar without navigation
 * <CustomCalendar
 *   currentMonth={new Date()}
 *   onPreviousMonth={() => {}}
 *   onNextMonth={() => {}}
 *   onDateClick={(date) => {}}
 *   getEventsForDate={() => []}
 *   eventColors={{}}
 *   showNavigation={false}
 *   size="small"
 * />
 */
const CustomCalendar = ({
  currentMonth,
  onPreviousMonth,
  onNextMonth,
  onDateClick,
  getEventsForDate,
  eventColors,
  activeDate,
  showWeekdays = true,
  showNavigation = true,
  className = "",
  size = "medium", // 'small', 'medium', 'large'
}) => {
  const generateCalendarDates = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();

    const firstDayOfWeek = firstDay.getDay();

    const prevMonth = new Date(year, month - 1, 0);
    const daysInPrevMonth = prevMonth.getDate();

    const dates = [];

    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      dates.push({
        date: daysInPrevMonth - i,
        isCurrentMonth: false,
        isToday: false,
        fullDate: new Date(year, month - 1, daysInPrevMonth - i),
      });
    }

    const today = new Date();
    for (let date = 1; date <= daysInMonth; date++) {
      const currentDate = new Date(year, month, date);
      const isToday = currentDate.toDateString() === today.toDateString();

      dates.push({
        date,
        isCurrentMonth: true,
        isToday,
        fullDate: currentDate,
      });
    }

    const totalDaysNeeded = CALENDAR_CONSTANTS.DAYS_IN_GRID;
    const remainingDays = totalDaysNeeded - dates.length;
    for (let date = 1; date <= remainingDays; date++) {
      dates.push({
        date,
        isCurrentMonth: false,
        isToday: false,
        fullDate: new Date(year, month + 1, date),
      });
    }

    return dates;
  };

  const getMonthDisplay = () => {
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return {
      month: monthNames[currentMonth.getMonth()],
      year: currentMonth.getFullYear(),
    };
  };

  const monthDisplay = getMonthDisplay();
  const calendarDates = generateCalendarDates();

  const handleDateClick = (fullDate) => {
    if (onDateClick) {
      onDateClick(fullDate);
    }
  };

  const calendarClass = `custom-calendar ${className} ${size}`;

  return (
    <div className={calendarClass}>
      <div className="calendar-center">
        {showNavigation && (
          <div className="calendar-header">
            <Tooltip anchorElement="target" position="top" openDelay={100}>
              <button className="nav-arrow prev-arrow" onClick={onPreviousMonth} title="Previous Month">
                <span>&lt;</span>
              </button>
            </Tooltip>
            <div className="month-year">
              <h3>
                {monthDisplay.month} {monthDisplay.year}
              </h3>
            </div>
            <Tooltip anchorElement="target" position="top" openDelay={100}>
              <button className="nav-arrow next-arrow" onClick={onNextMonth} title="Next Month">
                <span>&gt;</span>
              </button>
            </Tooltip>
          </div>
        )}
        <div className="calendar-grid">
          {showWeekdays && (
            <div className="calendar-weekdays">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                <div key={day} className="weekday">
                  {day}
                </div>
              ))}
            </div>
          )}
          <div className="calendar-dates">
            {calendarDates.map((dateObj, index) => {
              const events = dateObj.isCurrentMonth ? getEventsForDate(dateObj.date) : [];
              const isActive = activeDate && new Date(activeDate).toDateString() === dateObj.fullDate.toDateString();

              return (
                <div
                  key={index}
                  className={`calendar-date ${dateObj.isCurrentMonth ? "current-month" : "other-month"} ${dateObj.isToday ? "today" : ""} ${isActive ? "active" : ""}`}
                  onClick={() => handleDateClick(dateObj.fullDate)}
                >
                  <span className="date-number">{dateObj.date}</span>
                  {events.length > 0 && (
                    <div className="event-dots">
                      {events.slice(0, 3).map((event, eventIndex) => (
                        <div key={eventIndex} className="event-dot" style={{ backgroundColor: eventColors[event] }} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

CustomCalendar.propTypes = {
  currentMonth: PropTypes.instanceOf(Date).isRequired,
  onPreviousMonth: PropTypes.func.isRequired,
  onNextMonth: PropTypes.func.isRequired,
  onDateClick: PropTypes.func.isRequired,
  getEventsForDate: PropTypes.func.isRequired,
  eventColors: PropTypes.object.isRequired,
  activeDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  showWeekdays: PropTypes.bool,
  showNavigation: PropTypes.bool,
  className: PropTypes.string,
  size: PropTypes.oneOf(["small", "medium", "large"]),
};

CustomCalendar.defaultProps = {
  activeDate: null,
  showWeekdays: true,
  showNavigation: true,
  className: "",
  size: "medium",
};

export default CustomCalendar;
